import React, { useState, useEffect } from 'react';
import './Etape5.css';
import inspectionStorage from '../services/inspectionStorage';
import syncManager from '../services/syncManager';
import pendingPhotosService from '../services/pendingPhotosService';
import ObservationModal from './modals/ObservationModal';
import QuickPhotoButton from './QuickPhotoButton';

function Etape5({ mission, onBack, onSave }) {
  const [formData, setFormData] = useState({
    mesures: [],
    cuivre_nu_25: false,
    cuivre_isole_16: false,
    lep_canalisations_exterieures: false,
    lep_structures_metalliques: false,
    lep_canalisations_fluides: false,
    lep_gaines_tresses: false,
    lep_autres: ''
  });

  const [currentMesure, setCurrentMesure] = useState({
    configuration_reseau: '',
    reseau_conducteurs: '',
    localisation: '',
    designation: '',
    nature: '',
    methode_mesure: '',
    condition_mesure: '',
    valeur_max: '',
    valeur_mesuree: '',
    numero_obs: ''
  });

  const [editingIndex, setEditingIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  const [showObservationModal, setShowObservationModal] = useState(false);
  const [observationContext, setObservationContext] = useState(null);
  const [obsInitialPhotos, setObsInitialPhotos] = useState([]);
  const [obsPendingPhotoId, setObsPendingPhotoId] = useState(null);

  useEffect(() => {
    loadSavedData();
  }, []);

  const loadSavedData = async () => {
    try {
      const saved = await inspectionStorage.getStep(mission.id, 'etape_5_prises_terre');
      if (saved) {
        setFormData(saved);
        console.log('📦 Données Étape 5 récupérées depuis le cache');
      }
    } catch (error) {
      console.error('❌ Erreur chargement cache:', error);
    }
  };

  const saveStepData = async (data) => {
    const stepData = {
      ...data,
      timestamp: new Date().toISOString()
    };
    
    // 1. Sauvegarder en local (IndexedDB)
    await inspectionStorage.saveStep(mission.id, 'etape_5_prises_terre', stepData);
    
    // 2. 🔥 AUTO-SYNC vers Base44 (si en ligne)
    syncManager.syncStep(mission.id, 'etape_5_prises_terre', stepData);
  };

  const updateMesureField = (field, value) => {
    setCurrentMesure(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addMesure = async () => {
    if (currentMesure.localisation) {
      let updatedMesures;
      if (editingIndex !== null) {
        updatedMesures = [...formData.mesures];
        updatedMesures[editingIndex] = { ...currentMesure };
      } else {
        updatedMesures = [...formData.mesures, { ...currentMesure }];
      }
      
      const newFormData = {
        ...formData,
        mesures: updatedMesures
      };
      
      setFormData(newFormData);
      await saveStepData(newFormData);
      
      setCurrentMesure({
        configuration_reseau: '',
        reseau_conducteurs: '',
        localisation: '',
        designation: '',
        nature: '',
        methode_mesure: '',
        condition_mesure: '',
        valeur_max: '',
        valeur_mesuree: '',
        numero_obs: ''
      });
      
      setEditingIndex(null);
    }
  };

  const editMesure = (index) => {
    const mesure = formData.mesures[index];
    setCurrentMesure(mesure);
    setEditingIndex(index);
  };

  const cancelEdit = () => {
    setCurrentMesure({
      configuration_reseau: '',
      reseau_conducteurs: '',
      localisation: '',
      designation: '',
      nature: '',
      methode_mesure: '',
      condition_mesure: '',
      valeur_max: '',
      valeur_mesuree: '',
      numero_obs: ''
    });
    setEditingIndex(null);
  };

  const removeMesure = async (index) => {
    const updatedMesures = formData.mesures.filter((_, i) => i !== index);
    const newFormData = {
      ...formData,
      mesures: updatedMesures
    };
    setFormData(newFormData);
    await saveStepData(newFormData);
  };

  const updateField = async (field, value) => {
    const newFormData = {
      ...formData,
      [field]: value
    };
    setFormData(newFormData);
    await saveStepData(newFormData);
  };

  const buildMesureContext = (mesure, index) => ({
    step_origin: 'etape_5_prises_terre',
    localisation: mesure.localisation,
    element: `Prise de terre - ${mesure.designation || mesure.localisation}`,
    circuit: '',
    element_ref: {
      type: 'prise_terre',
      index: index,
      valeur_mesuree: mesure.valeur_mesuree,
      valeur_max: mesure.valeur_max,
    },
  });

  const openObsModalWith = (ctx, initialPhotos = [], pendingPhotoId = null) => {
    setObservationContext(ctx);
    setObsInitialPhotos(initialPhotos);
    setObsPendingPhotoId(pendingPhotoId);
    setShowObservationModal(true);
  };

  const openObservationForMesure = (mesure, index) => {
    openObsModalWith(buildMesureContext(mesure, index));
  };

  const handleQuickPhotoOpenObs = (photo, ctx) => {
    if (!photo) return;
    const url = photo.url || photo.blob_base64;
    openObsModalWith(ctx, url ? [url] : [], photo.id);
  };

  const handleCloseObsModal = () => {
    setShowObservationModal(false);
    setObservationContext(null);
    setObsInitialPhotos([]);
    setObsPendingPhotoId(null);
  };

  const handleSaveObservation = async (observationData) => {
    try {
      const etape11Data = await inspectionStorage.getStep(mission.id, 'etape_11_observations') || {};
      const currentObservations = etape11Data.observations || [];
      
      const dernierNumero = currentObservations.length > 0
        ? Math.max(...currentObservations.map(o => o.numero || 0))
        : 0;
      const nouveauNumero = dernierNumero + 1;
      
      const observation = {
        ...observationData,
        numero: nouveauNumero,
        created_date: observationData.created_date || new Date().toISOString(),
        photos: observationData.photos || []
      };
      
      const updatedObservations = [...currentObservations, observation];
      
      const obsStepData = {
        observations: updatedObservations,
        dernier_numero: nouveauNumero,
        timestamp: new Date().toISOString()
      };
      
      await inspectionStorage.saveStep(mission.id, 'etape_11_observations', obsStepData);
      
      // 🔥 AUTO-SYNC observations vers Base44
      syncManager.syncStep(mission.id, 'etape_11_observations', obsStepData);

      if (observationContext?.element_ref?.type === 'prise_terre') {
        const mesureIndex = observationContext.element_ref.index;
        const updatedMesures = [...formData.mesures];
        updatedMesures[mesureIndex] = {
          ...updatedMesures[mesureIndex],
          numero_obs: nouveauNumero.toString()
        };
        
        const newFormData = {
          ...formData,
          mesures: updatedMesures
        };
        setFormData(newFormData);
        await saveStepData(newFormData);
      }

      console.log('✅ Observation sauvegardée:', nouveauNumero);

      if (obsPendingPhotoId) {
        try {
          await pendingPhotosService.remove(mission.id, obsPendingPhotoId);
        } catch (e) {
          console.warn('[Etape5] suppression pending photo échouée :', e?.message || e);
        }
      }

      alert(`✅ Observation N°${nouveauNumero} créée avec succès !`);
    } catch (error) {
      console.error('❌ Erreur sauvegarde observation:', error);
      alert('Erreur lors de la sauvegarde de l\'observation');
    }

    handleCloseObsModal();
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveStepData(formData);
      
      console.log('✅ Étape 5 sauvegardée localement');
      
      if (onSave) {
        onSave(formData);
      }

      alert('✅ Étape 5 sauvegardée avec succès !');
      
      onBack();
      
    } catch (error) {
      console.error('❌ Erreur sauvegarde:', error);
      alert('❌ Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="etape-container">
      <div className="etape-header">
        <button className="back-button" onClick={onBack}>
          ← Retour
        </button>
        <div className="etape-title">
          <h1>Étape 5/14</h1>
          <p>Prises de terre</p>
        </div>
        <div></div>
      </div>

      <div className="etape-content">
        
        <div className="form-section prise-terre-form">
          <h2>⚡ Prise de terre</h2>
          
          <div className="info-grid">
            <div className="form-group">
              <label>Configuration du réseau de terre</label>
              <select
                value={currentMesure.configuration_reseau}
                onChange={(e) => updateMesureField('configuration_reseau', e.target.value)}
                className="form-select"
              >
                <option value="">-- Sélectionner --</option>
                <option value="interconnectees">Prises de terre interconnectées</option>
                <option value="non_interconnectees">Prises de terre non interconnectées</option>
                <option value="unique_bt">Prise de terre unique BT</option>
                <option value="autres_config">Autres</option>
              </select>
            </div>

            <div className="form-group">
              <label>Réseau des conducteurs de protection</label>
              <select
                value={currentMesure.reseau_conducteurs}
                onChange={(e) => updateMesureField('reseau_conducteurs', e.target.value)}
                className="form-select"
              >
                <option value="">-- Sélectionner --</option>
                <option value="incorp_toute_install">Incorporés dans toute l'installation</option>
                <option value="incorp_partir_armoires">Incorporés à partir des armoires</option>
                <option value="non_incorp_meme_parcours">Non incorporés, même parcours</option>
                <option value="non_incorp_jusqu_armoires">Non incorporés jusqu'aux armoires</option>
                <option value="autres_reseau">Autres</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="required">Localisation de la prise de terre</label>
            <input
              type="text"
              value={currentMesure.localisation}
              onChange={(e) => updateMesureField('localisation', e.target.value)}
              className="form-input"
              placeholder="Ex: Atelier mécanique"
            />
          </div>

          <div className="info-grid">
            <div className="form-group">
              <label>Désignation de la prise de terre</label>
              <select
                value={currentMesure.designation}
                onChange={(e) => updateMesureField('designation', e.target.value)}
                className="form-select"
              >
                <option value="">-- Sélectionner --</option>
                <option value="terre_masses_bt">Terre des masses BT</option>
                <option value="terre_masses_ht">Terre des masses HT</option>
                <option value="terre_masses_ht_neutre_bt">Terre des masses HT et neutre BT</option>
                <option value="terre_masses_ht_bt_neutre_bt">Terre des masses HT, BT et neutre BT</option>
                <option value="terre_neutre_bt_masses_ht">Terre du neutre BT et masses HT</option>
                <option value="terre_neutre_bt_masses_bt">Terre du neutre BT et masses BT</option>
              </select>
            </div>

            <div className="form-group">
              <label>Nature de la prise de terre</label>
              <select
                value={currentMesure.nature}
                onChange={(e) => updateMesureField('nature', e.target.value)}
                className="form-select"
              >
                <option value="">-- Sélectionner --</option>
                <option value="fond_fouille">Fond de Fouille</option>
                <option value="piquet_terre">Piquet de Terre</option>
                <option value="non_communique">Non Communiqué</option>
                <option value="ei">E.I.</option>
              </select>
            </div>
          </div>

          <div className="info-grid">
            <div className="form-group">
              <label>Méthode de mesure</label>
              <select
                value={currentMesure.methode_mesure}
                onChange={(e) => updateMesureField('methode_mesure', e.target.value)}
                className="form-select"
              >
                <option value="">-- Sélectionner --</option>
                <option value="terres_auxiliaires">Terres Auxiliaires</option>
                <option value="resistance_boucle">Résistance de Boucle</option>
              </select>
            </div>

            <div className="form-group">
              <label>Condition de mesure</label>
              <select
                value={currentMesure.condition_mesure}
                onChange={(e) => updateMesureField('condition_mesure', e.target.value)}
                className="form-select"
              >
                <option value="">-- Sélectionner --</option>
                <option value="barette_ouverte">Barette Ouverte</option>
                <option value="barette_fermee">Barette Fermée</option>
                <option value="ensemble_interconnecte">Ensemble Interconnecté</option>
              </select>
            </div>
          </div>

          <div className="info-grid">
            <div className="form-group">
              <label>Valeur max</label>
              <select
                value={currentMesure.valeur_max}
                onChange={(e) => updateMesureField('valeur_max', e.target.value)}
                className="form-select"
              >
                <option value="">-- Sélectionner --</option>
                <option value="1666">1666 Ohms</option>
                <option value="800">800 Ohms</option>
                <option value="100">100 Ohms</option>
                <option value="50">50 Ohms</option>
                <option value="30">30 Ohms</option>
                <option value="10">10 Ohms</option>
                <option value="1">1 Ohm</option>
              </select>
            </div>

            <div className="form-group">
              <label>Valeur mesurée (Ohms)</label>
              <input
                type="number"
                step="0.1"
                value={currentMesure.valeur_mesuree}
                onChange={(e) => updateMesureField('valeur_mesuree', e.target.value)}
                className="form-input"
                placeholder="Ex: 2.0"
              />
            </div>
          </div>

          <div className="form-group">
            <label>N° OBS</label>
            <input
              type="text"
              value={currentMesure.numero_obs}
              onChange={(e) => updateMesureField('numero_obs', e.target.value)}
              className="form-input"
              placeholder="Numéro d'observation"
              readOnly
            />
          </div>

          <div className="form-actions-inline">
            <button 
              onClick={addMesure}
              className="btn btn-success"
              disabled={!currentMesure.localisation}
            >
              ➕ {editingIndex !== null ? 'METTRE À JOUR LA PRISE DE TERRE' : 'AJOUTER UNE PRISE DE TERRE'}
            </button>
            {editingIndex !== null && (
              <button 
                onClick={cancelEdit}
                className="btn btn-secondary"
              >
                Annuler
              </button>
            )}
          </div>
        </div>

        {formData.mesures.length > 0 && (
          <div className="mesures-section">
            <h3>⚡ Mesures de prises de terre</h3>
            
            <div className="table-container">
              <table className="mesures-table">
                <thead>
                  <tr>
                    <th>Localisation</th>
                    <th>Désignation</th>
                    <th>Nature</th>
                    <th>Valeur max</th>
                    <th>Valeur mesurée</th>
                    <th>N° OBS</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.mesures.map((mesure, index) => (
                    <tr key={index}>
                      <td>{mesure.localisation}</td>
                      <td>{mesure.designation}</td>
                      <td>{mesure.nature}</td>
                      <td>{mesure.valeur_max} Ω</td>
                      <td>{mesure.valeur_mesuree} Ω</td>
                      <td>{mesure.numero_obs || '-'}</td>
                      <td className="actions-cell">
                        <button
                          onClick={() => editMesure(index)}
                          className="btn-icon btn-edit"
                          title="Modifier"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => openObservationForMesure(mesure, index)}
                          className="btn-icon btn-observation"
                          title="Ajouter une observation"
                        >
                          ⚠️
                        </button>
                        <QuickPhotoButton
                          missionId={mission?.id}
                          context={buildMesureContext(mesure, index)}
                          onOpenObservation={handleQuickPhotoOpenObs}
                          title="Prendre une photo de la prise de terre"
                        />
                        <button
                          onClick={() => removeMesure(index)}
                          className="btn-icon btn-delete"
                          title="Supprimer"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="form-section conducteur-section">
          <h2>🔌 Section du conducteur principal de protection</h2>
          
          <div className="checkbox-grid">
            <label className="checkbox-card">
              <input
                type="checkbox"
                checked={formData.cuivre_nu_25}
                onChange={(e) => updateField('cuivre_nu_25', e.target.checked)}
              />
              <span>25mm² Cuivre nu</span>
            </label>

            <label className="checkbox-card">
              <input
                type="checkbox"
                checked={formData.cuivre_isole_16}
                onChange={(e) => updateField('cuivre_isole_16', e.target.checked)}
              />
              <span>16mm² Cuivre isolé</span>
            </label>
          </div>
        </div>

        <div className="form-section lep-section">
          <h2>⚡ Description des LEP de l'installation</h2>
          
          <div className="checkbox-grid">
            <label className="checkbox-card">
              <input
                type="checkbox"
                checked={formData.lep_canalisations_exterieures}
                onChange={(e) => updateField('lep_canalisations_exterieures', e.target.checked)}
              />
              <span>Canalisations extérieures pénétrant dans l'établissement</span>
            </label>

            <label className="checkbox-card">
              <input
                type="checkbox"
                checked={formData.lep_structures_metalliques}
                onChange={(e) => updateField('lep_structures_metalliques', e.target.checked)}
              />
              <span>Structures métalliques du bâtiment</span>
            </label>

            <label className="checkbox-card">
              <input
                type="checkbox"
                checked={formData.lep_canalisations_fluides}
                onChange={(e) => updateField('lep_canalisations_fluides', e.target.checked)}
              />
              <span>Canalisations de fluides</span>
            </label>

            <label className="checkbox-card">
              <input
                type="checkbox"
                checked={formData.lep_gaines_tresses}
                onChange={(e) => updateField('lep_gaines_tresses', e.target.checked)}
              />
              <span>Gaines ou tresses métalliques des câbles de communication</span>
            </label>
          </div>

          <div className="form-group">
            <label>Autres :</label>
            <textarea
              value={formData.lep_autres}
              onChange={(e) => updateField('lep_autres', e.target.value)}
              className="form-textarea"
              rows={3}
              placeholder="Décrivez d'autres types de LEP..."
            />
          </div>
        </div>
      </div>

      <div className="etape-actions">
        <button 
          className="btn btn-secondary" 
          onClick={onBack}
          disabled={isSaving}
        >
          Annuler
        </button>
        <button 
          className="btn btn-primary" 
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? '💾 Sauvegarde...' : '💾 Sauvegarder et continuer'}
        </button>
      </div>

      {showObservationModal && (
        <ObservationModal
          isOpen={showObservationModal}
          onClose={handleCloseObsModal}
          onSave={handleSaveObservation}
          context={observationContext}
          initialPhotos={obsInitialPhotos}
          missionId={mission?.id}
        />
      )}
    </div>
  );
}

export default Etape5;